import { Box, Flex } from '@chakra-ui/layout';
import { Button, Card, CardBody, CardHeader } from '@chakra-ui/react';
import React from 'react';
import { useParams } from 'react-router';
import { Link } from 'react-router-dom';
import { z } from 'zod';

import { colors } from 'packages/common-frontend/colors';
import { Typography } from 'packages/common-frontend/components/Typography';
import { useSimpleColorMode } from 'packages/common-frontend/theme/utils';
import { useCreditFacilityOrThrow } from '~/contexts/CreditFacilitiesProvider';

const RouteParamsStruct = z.object({
  facilityId: z.string({ message: 'Missing implementationId' }),
});

type FacilityDetailCardProps = {
  title: string;
  to: string;
  children: React.ReactNode;
};

const FacilityDetailCard = ({
  title,
  to,
  children,
}: FacilityDetailCardProps) => {
  const mode = useSimpleColorMode();

  return (
    <Card width="20rem" variant="outline">
      <Button
        as={Link}
        colorScheme="v2-aurora"
        variant="link"
        to={to}
        textDecoration="none"
        _hover={{
          textDecoration: 'none',
          background: mode(colors.gray[50], colors.gray[800]),
        }}
      >
        <Box>
          <CardHeader>
            <Typography variant="section-title" trim>
              {title}
            </Typography>
          </CardHeader>
          <CardBody>
            <Typography variant="base-muted" TextProps={{ whiteSpace: 'wrap' }}>
              {children}
            </Typography>
          </CardBody>
        </Box>
      </Button>
    </Card>
  );
};

export const FacilityDetails = () => {
  const params = useParams();
  const { facilityId } = RouteParamsStruct.parse(params);

  const facility = useCreditFacilityOrThrow(facilityId);

  return (
    <Flex direction="column">
      <Typography variant="page-title">{facility.name}</Typography>
      <Flex gap="1.5rem" wrap="wrap">
        <FacilityDetailCard title="Facility Mechanics" to="facility-mechanics">
          Defines the overall loan facility structure, including the total
          financial commitment.
        </FacilityDetailCard>
        <FacilityDetailCard title="Parties" to="parties">
          Lists the key participants responsible for managing and executing the
          loan agreement.
        </FacilityDetailCard>
        <FacilityDetailCard title="Loan Mechanics" to="loan-mechanics">
          Describes the pricing and repayment terms that govern the loan&apos;s
          operation.
        </FacilityDetailCard>
      </Flex>
    </Flex>
  );
};
